import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Chip,
  Menu,
  MenuItem,
  TextField,
  Autocomplete,
  IconButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  useTheme,
  Modal,
  InputLabel,
  Snackbar,
  Alert,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  Edit,
  Delete as DeleteIcon,
  MoreHoriz,
  MoreVert,
} from "@mui/icons-material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import useStyles from "./styles";

// Filtro para o Autocomplete
const filter = createFilterOptions();

export default forwardRef(function StatusTags({
  // Paleta de cores (theme)
  colorOptions = [
    { label: "Verde", value: "colors.green.light" },
    { label: "Roxo", value: "colors.purple.medium" },
    { label: "Rosa", value: "colors.pink.medium" },
    { label: "Azul", value: "colors.blue.medium" },
    { label: "Vermelho", value: "colors.red.dark" },
    { label: "Laranja", value: "colors.orange.dark" },
    { label: "Amarelo", value: "colors.yellow.dark" },
    { label: "Cinza", value: "colors.gray.dark" },
  ],
  // Manipulação de tags
  initialTags = [],
  onTagCreate = () => { },
  onTagUpdate = () => { },
  onTagDelete = () => { },
  onTagSelect = () => { },
  // Estilização
  styles: customStyles = {},
  // Conteúdo exibido
  texts = {
    label: "Tag",
    placeholder: "Selecionar tag",
    searchPlaceholder: "Buscar ou criar tag",
    editTitle: "Editar Tag",
    deleteTitle: "Excluir",
    deleteConfirmation: "Tem certeza que deseja excluir a tag",
    nameLabel: "Nome da tag",
    colorLabel: "Cor",
    cancelButton: "Cancelar",
    saveButton: "Salvar",
    deleteButton: "Excluir",
    createButton: 'Criar "{tag}"',
  },
  selectedTagId: controlledSelectedId = null,
  loading = false,
  disabled = false,
  // Itens extras opcionais para o menu de ações (MoreVert) de cada tag.
  // Cada item: { label: string, icon?: ReactNode, onClick: (tag) => void }
  extraMenuItems = [],
  // Render-prop opcional para injetar conteúdo extra dentro do modal de edição da tag.
  // Recebe ({ tag }) e o resultado é renderizado acima dos botões de ação do modal.
  renderEditModalExtraContent = null,
}, ref) {
  const theme = useTheme();
  const defaultStyles = useStyles(theme);
  const [snackbar, setSnackbar] = useState(null);

  // Associa estilos padrão com personalizados
  const styles = { ...defaultStyles, ...customStyles };

  // Lista de tags
  const [tagList, setTagList] = useState(initialTags);
  useEffect(() => {
    setTagList(initialTags);
  }, [initialTags]);

  // Gerenciamento interno ou externo
  const isControlled = controlledSelectedId !== null;
  const [uncontrolledSelectedId, setUncontrolledSelectedId] = useState(null);

  const selectedIdTag = isControlled
    ? controlledSelectedId
    : uncontrolledSelectedId;
  const selectedTag = tagList?.find((tag) => tag?.id === selectedIdTag) || null;

  // States para menu e controles de edição
  const [openAutocompleteOptions, setOpenAutocompleteOptions] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);
  const [optionsMenuAnchor, setOptionsMenuAnchor] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedTagName, setEditedTagName] = useState("");
  const [colorTag, setColorTag] = useState("");
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Open options menu
  const handleOptionsMenuOpen = (event, tag) => {
    event?.stopPropagation();
    setCurrentTag(tag);
    setOptionsMenuAnchor(event?.currentTarget);
  };

  // Close options menu
  const handleOptionsMenuClose = () => {
    setOptionsMenuAnchor(null);
  };

  // Open edit modal
  const handleEditTagClick = () => {
    setEditedTagName(currentTag?.tagName);
    setColorTag(currentTag?.color || colorOptions[0]?.value);
    setEditModalOpen(true);
    handleOptionsMenuClose();
  };

  // Permite que o conteúdo extra (renderEditModalExtraContent) registre uma função
  // de salvar que será executada junto com o salvar da tag.
  const extraSaverRef = useRef(null);
  const registerSaver = (fn) => {
    extraSaverRef.current = typeof fn === "function" ? fn : null;
  };

  // Métodos imperativos expostos via ref (ex.: botão externo de atalho)
  useImperativeHandle(ref, () => ({
    openEditModal: (tagId) => {
      const tag = (tagList || []).find((t) => t?.id === tagId);
      if (!tag) return;
      setCurrentTag(tag);
      setEditedTagName(tag?.tagName || "");
      setColorTag(tag?.color || colorOptions[0]?.value);
      setEditModalOpen(true);
    },
    closeEditModal: () => setEditModalOpen(false),
  }), [tagList, colorOptions]);

  // Seleção de tag
  const handleSelectedTagChange = (id) => {
    if (!isControlled) {
      setUncontrolledSelectedId(id); // Atualiza estado interno se não-controlado
    }
    onTagSelect(id); // Chama callback para modo controlado
  };

  // Deletar a tag
  const deleteTag = async (id) => {
    try {
      // Chama execução no componente pai
      await onTagDelete(id);

      // Filtra o item excluído
      setTagList((prev) => prev?.filter((tag) => tag?.id !== id));
      if (selectedIdTag === id) handleSelectedTagChange(null);
      setOpenModalDelete(false);
      setCurrentTag(null);

      setSnackbar({
        children: `Sucesso ao remover ${texts?.label}`,
        severity: "success",
      });
    } catch (error) {
      console.error(`Failed to delete tag: `, error);
      setSnackbar({
        children: `Falha ao remover ${texts?.label}`,
        severity: "error",
      });
    }
  };

  // Editar a tag
  const editTagName = async (newTagName) => {
    try {
      const updatedTag = {
        ...currentTag,
        tagName: newTagName,
        color: colorTag,
      };

      await onTagUpdate(updatedTag);

      // Executa o saver registrado pelo conteúdo extra do modal (ex.: contatos de escalonamento)
      if (typeof extraSaverRef.current === "function") {
        await extraSaverRef.current();
      }

      setTagList((prev) =>
        prev?.map((tag) => (tag?.id === updatedTag?.id ? updatedTag : tag))
      );
      setEditModalOpen(false);
      setCurrentTag(null);

      setSnackbar({
        children: `Sucesso ao atualizar ${texts?.label}`,
        severity: "success",
      });
    } catch (error) {
      console.error(`Failed to edit tag: `, error);
      setSnackbar({
        children: `Falha ao atualizar ${texts?.label}`,
        severity: "error",
      });
    }
  };

  // Criar nova tag
  const createTag = async (tagName) => {
    try {
      const newId = crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2) + Date.now().toString(36);
      const randomColor =
        colorOptions[Math.floor(Math.random() * colorOptions?.length)]?.value;
      const newTag = {
        id: newId,
        tagName: tagName,
        color: randomColor,
        createAt: new Date().toISOString(),
      };

      // Chama execução no componente pai
      const result = await onTagCreate(newTag);
      // Atualiza de acordo com o retorno
      handleSelectedTagChange(result?.id || newTag?.id);
      setTagList((prev) => [...prev, result || newTag]);
      setInputValue("");

      setSnackbar({
        children: `Sucesso ao criar ${texts?.label}`,
        severity: "success",
      });
    } catch (error) {
      console.error(`Failed to create tag: `, error);
      setSnackbar({
        children: `Falha ao criar ${texts?.label}`,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      {/* Campo de seleção de Tag com Autocomplete */}
      <Box sx={styles.inputContainer}>
        {loading && <LinearProgress sx={styles.linearProgress} />}

        <Autocomplete
          open={!!openAutocompleteOptions || !!optionsMenuAnchor}
          PopperProps={{
            placement: "bottom-start",
          }}
          disabled={disabled}
          value={selectedTag}
          onChange={(event, newValue) => {
            if (newValue?.inputValue) {
              // Criar nova tag
              createTag(newValue?.inputValue);
            } else {
              // Selecionar tag existente
              handleSelectedTagChange(newValue ? newValue?.id : null);
            }
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            const { inputValue } = params;

            // Verifica se o input existe e se não é uma tag existente
            const isExisting = options?.some(
              (option) =>
                inputValue?.toLowerCase() === option?.tagName?.toLowerCase()
            );

            if (inputValue !== "" && !isExisting) {
              filtered.push({
                inputValue,
                tagName: texts?.createButton?.replace("{tag}", inputValue),
              });
            }

            return filtered;
          }}
          options={tagList}
          getOptionLabel={(option) => {
            // Valor selecionado
            if (typeof option === "string") return option;
            // Nova opção de criação
            if (option?.inputValue) return option?.inputValue;
            // Tag existente
            return option?.tagName;
          }}
          renderOption={(props, option) => {
            // Se for uma opção de criação
            if (option?.inputValue) {
              return (
                <li {...props} style={styles.createItem}>
                  {texts?.createButton?.replace("{tag}", option.inputValue)}
                </li>
              );
            }

            // Se for uma tag existente
            return (
              <li {...props} style={styles.tagListItem}>
                <Chip
                  size="small"
                  label={option?.tagName}
                  sx={{
                    bgcolor: `${option?.color || "chip.main.arquive"}`,
                    ...styles.selectedTag,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => handleOptionsMenuOpen(e, option)}
                  sx={styles.optionsButton}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onClick={() => {
                setOpenAutocompleteOptions(true);
              }}
              onBlur={() => {
                setOpenAutocompleteOptions(false);
              }}
              sx={styles.inputTag}
              label={texts?.placeholder}
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading || disabled}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedTag ? (
                  <Chip
                    size="small"
                    label={selectedTag?.tagName}
                    sx={{
                      bgcolor: `${selectedTag?.color || "chip.main.arquive"}`,
                      ...styles.selectedTag,
                    }}
                  />
                ) : null,
                ...(selectedTag && {
                  style: { ...params?.InputProps?.style, color: "transparent" },
                }),
              }}
              inputProps={{
                ...params.inputProps,
                ...(selectedTag && { style: { opacity: 0 } }),
              }}
            />
          )}
          clearOnBlur
          handleHomeEndKeys
          selectOnFocus
        />
      </Box>

      {/* Menu de ações (Editar/Excluir)  */}
      <Menu
        anchorEl={optionsMenuAnchor}
        open={Boolean(optionsMenuAnchor)}
        onClose={handleOptionsMenuClose}
        anchorOrigin={styles.actionMenuAnchorOrigin}
        transformOrigin={styles.actionMenuTransformOrigin}
      >
        <MenuItem onClick={handleEditTagClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>{texts?.editButton || "Editar"}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenModalDelete(true);
            handleOptionsMenuClose();
          }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>
            {texts?.deleteButton || "Excluir"}
          </ListItemText>
        </MenuItem>
        {Array.isArray(extraMenuItems) && extraMenuItems.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              const tag = currentTag;
              handleOptionsMenuClose();
              item?.onClick?.(tag);
            }}
          >
            {item?.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText>{item?.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Modal de confirmação para deletar */}
      <Modal
        open={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        aria-labelledby="modal-delete-title"
        aria-describedby="modal-delete-description"
      >
        <Box sx={styles.deleteModalContainer}>
          <Typography id="modal-delete-title" gutterBottom>
            {texts?.deleteConfirmation} "{currentTag?.tagName}"?
          </Typography>
          <Box sx={styles.deleteModalButtons}>
            <Button
              variant="outlined"
              onClick={() => setOpenModalDelete(false)}
            >
              {texts?.cancelButton}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteTag(currentTag?.id)}
            >
              {texts?.deleteButton}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal de edição */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        aria-labelledby="modal-edit-title"
        aria-describedby="modal-edit-description"
      >
        <Box sx={{
          ...styles.editModalContainer,
          ...(typeof renderEditModalExtraContent === "function"
            ? { width: { xs: "95vw", md: 760 }, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }
            : {}),
        }}>
          <Typography variant="h6" gutterBottom id="modal-edit-title">
            {texts?.editTitle}
          </Typography>

          <TextField
            label={texts?.nameLabel}
            value={editedTagName}
            onChange={(e) => setEditedTagName(e?.target?.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <Autocomplete
            options={colorOptions}
            value={
              colorOptions?.find((option) => option?.value === colorTag) || null
            }
            onChange={(e, newValue) => setColorTag(newValue?.value)}
            getOptionLabel={(option) => option?.label}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={styles.editModalColorBox}>
                <Box
                  sx={{
                    backgroundColor: option?.value,
                    ...styles.editModalSelectedColor,
                  }}
                />
                <Typography>{option?.label}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={texts?.colorLabel}
                fullWidth
                InputProps={{
                  ...params?.InputProps,
                  size: "small",
                  startAdornment: colorTag ? (
                    <Box
                      sx={{
                        backgroundColor: colorTag,
                        ...styles.editModalColorOption,
                      }}
                    />
                  ) : null,
                }}
              />
            )}
          />

          {typeof renderEditModalExtraContent === "function" && (
            <Box sx={{ mt: 2 }}>
              {renderEditModalExtraContent({ tag: currentTag, registerSaver })}
            </Box>
          )}

          <Box sx={styles.editModalButtons}>
            <Button variant="outlined" onClick={() => setEditModalOpen(false)}>
              {texts?.cancelButton}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                editTagName(editedTagName);
              }}
            >
              {texts?.saveButton}
            </Button>
          </Box>
        </Box>
      </Modal>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
});
